import {
	USER_LOGIN_REQUEST,
	USER_LOGIN_SUCCESS,
	USER_LOGIN_FAIL,
	USER_LOGOUT,
	USER_REGISTER_REQUEST,
	USER_REGISTER_SUCCESS,
	USER_REGISTER_FAIL,
} from '../constants/userConstants'

import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from "firebase/auth"
import { firebaseAuth } from "../firebase/firebaseClient"

function mapFirebaseAuthError(error, fallback) {
	const code = String(error?.code || "")
	const lookup = {
		"auth/invalid-credential": "Invalid email or password.",
		"auth/wrong-password": "Invalid email or password.",
		"auth/user-not-found": "Invalid email or password.",
		"auth/invalid-email": "Please enter a valid email address.",
		"auth/user-disabled": "This account is disabled. Contact support.",
		"auth/email-already-in-use": "An account with this email already exists.",
		"auth/weak-password": "Password is too weak. Use at least 6 characters.",
		"auth/network-request-failed": "Network error. Check your connection and try again.",
		"auth/too-many-requests": "Too many attempts. Please try again in a few minutes.",
	}
	return lookup[code] || fallback || "Something went wrong. Please try again."
}

async function buildUserPayloadFromFirebaseUser(user) {
	const token = await user.getIdToken()
	const tokenResult = await user.getIdTokenResult(true)
	const is_admin = tokenResult.claims.admin === true
	return {
		uid: user.uid,
		email: user.email,
		idToken: token,
		is_admin,
	}
}

/** Refresh `is_admin` from custom claims after page reload (localStorage may be stale). */
export const hydrateUserInfoFromFirebase = () => async (dispatch) => {
	const user = firebaseAuth.currentUser
	if (!user) return
	try {
		const data = await buildUserPayloadFromFirebaseUser(user)
		const raw = localStorage.getItem("userInfo")
		const prev = raw ? JSON.parse(raw) : {}
		// Always overwrite is_admin from live claims (clears stale DEV bypass / old localStorage).
		const next = { ...prev, ...data, is_admin: data.is_admin === true }
		localStorage.setItem("userInfo", JSON.stringify(next))
		dispatch({ type: USER_LOGIN_SUCCESS, payload: next })
	} catch {
		// ignore
	}
}

export const login = (username, password) => async (dispatch) => {
	try {
		dispatch({
			type: USER_LOGIN_REQUEST,
		})
		
		const cred = await signInWithEmailAndPassword(firebaseAuth, username, password)
		const data = await buildUserPayloadFromFirebaseUser(cred.user)

		dispatch({
			type: USER_LOGIN_SUCCESS,
			payload: data,
		})

		localStorage.setItem('userInfo', JSON.stringify(data))
	} catch (error) {
		dispatch({
			type: USER_LOGIN_FAIL,
			payload: mapFirebaseAuthError(error, "Login failed. Please try again."),
		})
	}
}

export const logout = () => (dispatch) => {
	// Clear all client-side auth so deep links (e.g. /homepage) cannot reuse a session
	localStorage.removeItem('userInfo')
	localStorage.removeItem('authToken')
	localStorage.removeItem('mockAuthToken')
	localStorage.removeItem('merchant_id')
	localStorage.removeItem('access_token')
	localStorage.removeItem('userId')
	try {
		signOut(firebaseAuth)
	} catch (e) {
		// ignore
	}
	dispatch({ type: USER_LOGOUT })
}

export const register = (username, email, password) => async (dispatch) => {
	try {
		dispatch({
			type: USER_REGISTER_REQUEST,
		})
		
		// Firebase Auth uses email/password; we treat `email` as the login identifier.
		const cred = await createUserWithEmailAndPassword(firebaseAuth, email, password)
		const data = await buildUserPayloadFromFirebaseUser(cred.user)

		dispatch({
			type: USER_REGISTER_SUCCESS,
			payload: {
				message: "Account created! Let's set up your business.",
				...data,
			},
		})

		// Keep app’s existing storage key, but now it contains Firebase info.
		localStorage.setItem("userInfo", JSON.stringify(data))
	} catch (error) {
		dispatch({
			type: USER_REGISTER_FAIL,
			payload: mapFirebaseAuthError(error, "Registration failed. Please try again."),
		})
	}
}
